import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardListIcon, CornerDownLeftIcon, ListChecksIcon, SearchIcon, UserIcon, XIcon } from 'lucide-react';
import { Customer } from './CustomerCard';
import { Task } from './TaskCard';
import { QualityReport } from '../types/quality';
import { SearchResult, SearchResultKind, groupResults, searchEverything } from '../utils/search';
interface TopBarProps {
  customers: Customer[];
  tasks: Task[];
  qualityReports: QualityReport[];
  isAdmin: boolean;
  onSelectResult: (result: SearchResult) => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const KIND_ICON: Record<SearchResultKind, typeof UserIcon> = {
  customer: UserIcon,
  task: ClipboardListIcon,
  quality: ListChecksIcon,
  page: CornerDownLeftIcon
};
export function TopBar({
  customers,
  tasks,
  qualityReports,
  isAdmin,
  onSelectResult
}: TopBarProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => searchEverything({
    query,
    customers,
    tasks,
    qualityReports,
    isAdmin
  }), [query, customers, tasks, qualityReports, isAdmin]);
  const grouped = useMemo(() => groupResults(results), [results]);

  // Reset the highlighted row whenever the result set changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Cmd/Ctrl+K focuses search from anywhere.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Close the dropdown on an outside click.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);
  const commit = (result: SearchResult) => {
    onSelectResult(result);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(i => (i + 1) % results.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(i => (i - 1 + results.length) % results.length);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(results[activeIndex]);
    }
  };
  const showDropdown = isOpen && query.trim().length >= 2;
  let flatIndex = -1;
  return <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/90 backdrop-blur" data-id="element-813" data-mp="2lcdjzj813a1o">
      <div className="flex items-center gap-3 px-4 py-3 sm:px-6 lg:px-10" data-id="element-814" data-mp="2qb6wb8bkwv09">
        <span className="flex-shrink-0 text-base font-bold tracking-tight text-stone-900 lg:hidden" data-id="element-815" data-mp="2q940g7nqw7nm">
          Lykke-EL
        </span>

        <div ref={containerRef} className="relative min-w-0 flex-1 lg:max-w-xl" data-id="element-816" data-mp="2zbd88prmpmxb">
          <label className="relative block" data-id="element-817" data-mp="342lhnlsyl8ah">
            <span className="sr-only" data-id="element-818" data-mp="343wmzq21yo3p">Søg</span>
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" data-id="element-819" data-mp="391op1cwevqlm" />
            <input ref={inputRef} value={query} onChange={e => {
            setQuery(e.target.value);
            setIsOpen(true);
          }} onFocus={() => setIsOpen(true)} onKeyDown={handleKeyDown} placeholder="Søg kunder, opgaver, rapporter…" role="combobox" aria-expanded={showDropdown} aria-controls="global-search-results" className="w-full rounded-lg border border-stone-200 bg-stone-50 py-2 pl-9 pr-16 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500" data-id="element-820" data-mp="3e1b13j0i70su" />
            {query ? <button onClick={() => {
            setQuery('');
            inputRef.current?.focus();
          }} aria-label="Ryd søgning" className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-821" data-mp="3rvddfiiw463b">
                <XIcon className="h-4 w-4" data-id="element-822" data-mp="1g2pyj3ej5c6g" />
              </button> : <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-stone-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-stone-400 sm:block" data-id="element-823" data-mp="1okidv7yvu3ep">
                ⌘K
              </kbd>}
          </label>

          <AnimatePresence data-id="element-824" data-mp="1jlf8fmezpv67">
            {showDropdown && <motion.div id="global-search-results" role="listbox" initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.15,
            ease: EASE
          }} className="absolute left-0 right-0 top-full z-40 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-stone-200 bg-white py-1.5 shadow-lg" data-id="element-825" data-mp="1jlxd97fpxhpq">
                {results.length === 0 ? <p className="px-4 py-6 text-center text-sm text-stone-500" data-id="element-826" data-mp="2q8au5zvzi3zo">
                    Ingen resultater for “{query}”
                  </p> : grouped.map(group => <div key={group.group} data-id="element-827" data-mp="2zxzl9k5wz2bs">
                      <h3 className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-828" data-mp="2zy98x13hkxeb">
                        {group.group}
                      </h3>
                      <ul data-id="element-829" data-mp="2qw9ri7ok7rau">
                        {group.items.map(result => {
                  flatIndex += 1;
                  const isActive = flatIndex === activeIndex;
                  const Icon = KIND_ICON[result.kind];
                  return <li key={result.id} role="option" aria-selected={isActive} data-id="element-830" data-mp="2c96ue245fko1">
                              <button onMouseEnter={() => setActiveIndex(results.indexOf(result))} onClick={() => commit(result)} className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${isActive ? 'bg-brand-50' : 'hover:bg-stone-50'}`} data-id="element-831" data-mp="279tzcu34j7ad">
                                <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${isActive ? 'bg-brand-100 text-brand-700' : 'bg-stone-100 text-stone-500'}`} data-id="element-832" data-mp="mrjd2smc114g">
                                  <Icon className="h-4 w-4" data-id="element-833" data-mp="3z7fej6mio0n" />
                                </span>
                                <span className="min-w-0 flex-1" data-id="element-834" data-mp="8sjr5sq7f0bz">
                                  <span className="block truncate text-sm font-medium text-stone-900" data-id="element-835" data-mp="37g7if5lo48hj">
                                    {result.title}
                                  </span>
                                  {result.subtitle && <span className="block truncate text-xs text-stone-500" data-id="element-836" data-mp="25uudi022fghx">
                                      {result.subtitle}
                                    </span>}
                                </span>
                                {result.reference && <span className="flex-shrink-0 font-mono text-xs text-stone-400" data-id="element-837" data-mp="2orc3abxct6bj">
                                    {result.reference}
                                  </span>}
                              </button>
                            </li>;
                })}
                      </ul>
                    </div>)}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </header>;
}